<script setup lang="ts">
import { t } from '@/utils/i18n';
import type { DropdownMenuItem } from '@nuxt/ui';

withDefaults(defineProps<{ compact?: boolean }>(), { compact: false });

const bots = useBotStore();
const settings = useSettingsStore();
const router = useRouter();
const liveRefresh = useTrxLiveRefresh();
const { available, refreshing, result } = liveRefresh;
const connection = computed(() => {
  if (!bots.hasBots || !bots.activeBot?.isBotLoggedIn) return t('Page login required');
  return bots.activeBot.isBotOnline ? t('Page connected') : t('Page connection needs checking');
});
const connected = computed(
  () => bots.hasBots && bots.activeBot?.isBotLoggedIn && bots.activeBot.isBotOnline,
);
const refreshLabel = computed(() =>
  refreshing.value
    ? t('Reading…')
    : result.value === 'updated'
      ? t('Updated')
      : result.value === 'failed'
        ? t('Retry live refresh')
        : result.value === 'busy'
          ? t('Retry shortly')
          : t('Refresh live data'),
);
async function logout() {
  bots.removeBot(bots.selectedBot);
  await router.push('/login');
}
const menu = computed<DropdownMenuItem[][]>(() => [
  [{ label: `${t('UI Version:')} ${settings.uiVersion}`, disabled: true }],
  [
    {
      label: t('Connection and display settings'),
      icon: 'i-mdi-cog',
      onSelect: () => router.push('/settings'),
    },
  ],
  ...(bots.hasBots
    ? [
        [
          { label: t('Log out of this page'), icon: 'i-mdi-logout', onSelect: logout },
          { label: t('Server strategy continues after logout.'), disabled: true },
        ],
      ]
    : []),
]);
</script>

<template>
  <div
    class="trx-header-controls flex flex-wrap items-center justify-center gap-3 text-sm"
    :class="{ 'is-compact': compact }"
    :aria-label="t('TRX console controls')"
  >
    <span
      class="inline-flex items-center gap-1.5 whitespace-nowrap"
      :title="t('Browser connection only; strategy health is shown in Live operation.')"
    >
      <span
        class="size-2 rounded-full"
        :class="connected ? 'bg-success' : 'bg-warning'"
        aria-hidden="true"
      />
      <span :class="{ 'sr-only': compact }">{{ connection }}</span>
    </span>
    <UButton
      v-if="available"
      color="neutral"
      variant="outline"
      size="sm"
      icon="mdi:refresh"
      :disabled="refreshing"
      :loading="refreshing"
      :title="t('Refresh live data (read only)')"
      :aria-label="refreshLabel"
      @click="liveRefresh.refresh"
    >
      <span :class="{ 'sr-only': compact }">{{ refreshLabel }}</span>
    </UButton>
    <span v-if="available && result" role="status" class="sr-only">{{ refreshLabel }}</span>
    <UDropdownMenu :items="menu" size="lg">
      <UButton
        color="neutral"
        variant="ghost"
        size="sm"
        :trailing-icon="compact ? undefined : 'mdi:chevron-down'"
        :icon="compact ? 'mdi:dots-vertical' : undefined"
        :aria-label="t('TRX console menu')"
        ><span v-if="!compact">TRX</span></UButton
      >
    </UDropdownMenu>
  </div>
</template>

<style scoped>
.is-compact {
  flex-wrap: nowrap;
  gap: 6px;
}
.is-compact :deep(button) {
  min-height: 44px;
  min-width: 44px;
  justify-content: center;
}
</style>
